<!-- A component that can handle allowing upload of single or multiple files with max allowed count -->
<!-- It will allow presenting current files be they images or other files -->
<!-- It will accept current files then when files here change it will call the callback allowing the parent to handle the files -->
<!-- There will be an option to allow immediate creation or the parent can rely on the callback and its own form to submit the files -->
<!-- Similarly deletion can be handled instantly (with a path or callback) or deferred to the parent to handle when the form is submitted -->
<script>
  import Text from '@components/general/text.svelte'
  import FileIcon from '@components/general/file-icon.svelte'
  import Image from '@components/general/image.svelte'
  import Button from '@components/buttons/button.svelte'
  import { cn } from '@lib/utils'
  import { filesize } from 'filesize'
  import { toast } from 'svelte-sonner'
  import { CheckCircle, Trash2 } from 'lucide-svelte'

  let { 
    label,
    files = [],
    class: className,
    disabled = false,
    required = false,
    inputText = "Drag and drop files here or click to upload",
    maxCount = 10,
    showInputIfFull = true,
    onChange
  } = $props()

  let current_files = $state([])

  // Sync with files prop when it changes (e.g. when opening edit modal with existing resource)
  $effect(() => {
    current_files = files ?? []
  })

  let slots_full = $derived(current_files.length >= maxCount)

  let dragged_over = $state(false)
  let files_dragged = $state(0)
  
  let remaining_slots = $derived(maxCount - current_files.length)
  let valid_drop = $derived(dragged_over && files_dragged <= remaining_slots)
  let invalid_drop = $derived(dragged_over && files_dragged > remaining_slots)

  // Grid responds to container width via @container queries
  // Single column by default, expanding based on available space
  let grid_class = $derived(
    maxCount === 1
      ? "grid grid-cols-1 gap-2"
      : maxCount === 2
        ? "grid grid-cols-1 @sm:grid-cols-2 gap-2"
        : "grid grid-cols-1 @sm:grid-cols-2 @md:grid-cols-3 gap-2"
  )

  function handleDragEnter(e) {
    files_dragged = e.dataTransfer?.items?.length || 0
    dragged_over = true
  }

  function handleFileInput(e) {
    let new_files = Array.from(e.target.files || [])

    if (new_files.length === 0) return
    
    const new_count = new_files.length + current_files.length

    if (new_count > maxCount) {
      toast.error(`You can only upload up to ${maxCount} files`)
      e.preventDefault()
      return
    }
    
    new_files = new_files.map(file => {
      return {
        id: crypto.randomUUID(),
        name: file.name,
        content_type: file.type,
        byte_size: file.size,
        url: URL.createObjectURL(file),
        file: file,
        _new: true
      }
    })
    
    current_files = [...current_files, ...new_files]

    onChange?.(current_files)

    e.target.value = ''
  }

  function handleDrop() {
    dragged_over = false
    files_dragged = 0
  }

  function handleDelete(file) {
    current_files = current_files.filter(f => f.id !== file.id)
    onChange?.(current_files)
  }

  function handleDeleteAll() {
    current_files = []
    onChange?.(current_files)
  }

</script>

<div class={cn("@container flex flex-col", className)}>

  {#if label}
    <Text variant="label" text={label} class="mb-1.5" />
  {/if}

  {#if showInputIfFull || !slots_full}
    <section 
      aria-label="File upload"
      ondragenter={handleDragEnter}
      ondragover={() => dragged_over = true}
      ondragleave={() => dragged_over = false}
      ondrop={handleDrop}
      class={cn("border border-dashed border-gray-200 rounded-md p-4 flex items-center justify-center cursor-pointer relative mb-3", 
      slots_full ? "opacity-80 cursor-not-allowed" : "",
      valid_drop ? "border-gray-400 bg-gray-50/50" : "",
      invalid_drop ? "border-red-600 bg-red-50/50" : "")}>
      
      <!-- The text to display based on the state -->
      {#if slots_full}
        <div class="text-gray-400 text-smaller flex items-center gap-1">
          Maximum number of files reached
          <CheckCircle size={16} strokeWidth={1} class="text-gray-400" />
        </div>
      {:else if invalid_drop}
        <span class="text-red-600 text-smaller">Only {remaining_slots} slot(s) remaining!</span>
      {:else if valid_drop}
        <span class="text-gray-500/80 text-smaller">Drop {files_dragged} files!</span>
    {:else}
        <span class="text-gray-500/80 text-smaller">{inputText} (max: {maxCount})</span>
      {/if}

      <!-- The "hidden" input -->
      <input type="file" multiple disabled={slots_full} {required}
        class={cn("absolute inset-0 opacity-0 cursor-pointer", valid_drop ? "cursor-pointer" : "cursor-not-allowed")} 
        oninput={handleFileInput} />
    </section>
  {/if}

  <!-- The current files -->
  <section class={cn("w-full", grid_class)}>
    
    {#each current_files as file (file.id ?? file.url ?? file.name ?? crypto.randomUUID())}

      {@const is_image = file.content_type.startsWith('image/')}

      <div class="flex items-center gap-2 p-2 border border-gray-200 rounded-md relative">
        
        <!-- The preview image or icon -->
        {#if is_image}
          <div class="size-16 aspect-video rounded-md overflow-hidden">
            <Image src={file.url} alt={file.name} class="size-16 rounded-md object-contain" />
          </div>
        {:else}
          <FileIcon {file} size={26} strokeWidth={1} class="text-gray-500/80" />
        {/if}

        <!-- The file details -->
        <div class="flex flex-col text-xs text-gray-500/80">
          <span class="max-w-40 truncate">{file.name}</span>
          <span>{file.byte_size ? filesize(file.byte_size) : "Unknown"}</span>
          <Button variant="naked" Icon={Trash2} onclick={() => handleDelete(file)}
            class="absolute top-2 right-2 text-gray-500/80 hover:text-gray-700" 
            iconProps={{ size: 12 }} />
        </div>

      </div>

    {/each}

  </section>

  {#if current_files.length > 0 && maxCount > 1}
    <Button variant="naked" 
      text="Clear all files" Icon={Trash2}
      class="text-gray-500/80 hover:underline text-xs mt-4 ml-auto" iconProps={{ size: 12 }} 
      onclick={() => handleDeleteAll()} />
  {/if}

</div>